import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
	AddOutlined as AddOutlinedIcon,
	Clear as ClearIcon,
	Search as SearchIcon,
} from "@mui/icons-material";
import {
	Autocomplete,
	Box,
	Button,
	FormControl,
	Grid,
	IconButton,
	InputLabel,
	Menu,
	MenuItem,
	Paper,
	Select,
	SelectChangeEvent,
	TextField,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import { addMonths, endOfMonth, format, parseISO, startOfMonth, subDays, subMonths, subYears } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { IListagemUsuario, userService } from "../../../shared/services/api/user/userService";
import { RangeStates } from "../../../shared/utils";

type ManagerOption = {
	id: string;
	label: string;
};

type PeriodType = "indefinido" | "15_dias" | "mes_anterior" | "1_mes" | "proximo_mes" | "2_meses" | "3_meses" | "1_ano" | "personalizado";

const INDEFINITE_START_DATE = "2000-01-01";

function getDateRangeFromPeriod(period: PeriodType): [string, string] {
	const now = new Date();
	let start = now;
	let end = now;

	switch (period) {
		case "indefinido":
			return [INDEFINITE_START_DATE, format(endOfMonth(now), "yyyy-MM-dd")];
		case "15_dias":
			start = subDays(now, 15);
			end = now;
			break;
		case "mes_anterior":
			start = startOfMonth(subMonths(now, 1));
			end = endOfMonth(subMonths(now, 1));
			break;
		case "1_mes":
			start = startOfMonth(now);
			end = endOfMonth(now);
			break;
		case "proximo_mes":
			start = startOfMonth(addMonths(now, 1));
			end = endOfMonth(addMonths(now, 1));
			break;
		case "2_meses":
			start = startOfMonth(subMonths(now, 1));
			end = endOfMonth(now);
			break;
		case "3_meses":
			start = startOfMonth(subMonths(now, 2));
			end = endOfMonth(now);
			break;
		case "1_ano":
			start = startOfMonth(subYears(now, 1));
			end = endOfMonth(now);
			break;
		case "personalizado":
		default:
			return [format(startOfMonth(now), "yyyy-MM-dd"), format(endOfMonth(now), "yyyy-MM-dd")];
	}

	return [format(start, "yyyy-MM-dd"), format(end, "yyyy-MM-dd")];
}

export interface ToolbarListagemClientesProps {
	status: string;
	origem: string;
	uf: string;
	managerId: string;
	dataIni: string;
	dataFim: string;
	textSearch?: string;
	onChangeTextSearch?: (value: string) => void;
	onClearTextSearch?: () => void;

	showStatus?: boolean;
	showOrigem?: boolean;
	showUF?: boolean;
	showManager?: boolean;
	showPeriodo?: boolean;
	showInputSearch?: boolean;

	onChangeStatus: (value: string) => void;
	onChangeOrigem: (value: string) => void;
	onChangeUF: (value: string) => void;
	onChangeManagerId: (value: string) => void;
	onChangeDataIni: (value: string) => void;
	onChangeDataFim: (value: string) => void;

	onClickPesquisar: () => void;

	showButtonNew?: boolean;
	onClickNew?: () => void;
	textoButtonNew?: string;

	showContractsMenuItem?: boolean;
	contractsEnabled?: boolean;
	onClickContracts?: () => void;

	showTransferMenuItem?: boolean;
	transferEnabled?: boolean;
	onClickTransfer?: () => void;
}

export function ToolbarListagemClientes({
	status,
	origem,
	uf,
	managerId,
	dataIni,
	dataFim,
	textSearch = "",
	onChangeTextSearch,
	onClearTextSearch,
	showStatus = true,
	showOrigem = true,
	showUF = true,
	showManager = false,
	showPeriodo = true,
	showInputSearch = true,
	onChangeStatus,
	onChangeOrigem,
	onChangeUF,
	onChangeManagerId,
	onChangeDataIni,
	onChangeDataFim,
	onClickPesquisar,
	showButtonNew = false,
	onClickNew,
	textoButtonNew = "Novo",
	showContractsMenuItem = true,
	contractsEnabled = false,
	onClickContracts,
	showTransferMenuItem = true,
	transferEnabled = false,
	onClickTransfer,
}: ToolbarListagemClientesProps) {
	const theme = useTheme();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

	const [periodType, setPeriodType] = useState<PeriodType>("indefinido");

	const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
	const menuOpen = Boolean(menuAnchorEl);

	const [managerSearch, setManagerSearch] = useState("");
	const [managerOptions, setManagerOptions] = useState<ManagerOption[]>([]);
	const [managerLoading, setManagerLoading] = useState(false);

	useEffect(() => {
		if (!showManager) return;

		const cleaned = typeof managerSearch === "string" ? managerSearch.trim() : "";
		if (!cleaned) {
			setManagerOptions([]);
			return;
		}

		const timeout = setTimeout(() => {
			setManagerLoading(true);
			userService.getAdminManagers(cleaned).then((result) => {
				if (!(result instanceof Error)) {
					const options = (result.data ?? []).map((user: IListagemUsuario) => ({
						id: user.id,
						label: user.name,
					}));
					setManagerOptions(options);
				}
				setManagerLoading(false);
			});
		}, 400);

		return () => clearTimeout(timeout);
	}, [managerSearch, showManager]);

	const selectedManager = useMemo(() => {
		if (!managerId) return null;
		return managerOptions.find((o) => o.id === managerId) ?? null;
	}, [managerId, managerOptions]);

	const handlePeriodChange = (e: SelectChangeEvent) => {
		const value = e.target.value as PeriodType;
		setPeriodType(value);

		if (value !== "personalizado") {
			const [newStart, newEnd] = getDateRangeFromPeriod(value);
			onChangeDataIni(newStart);
			onChangeDataFim(newEnd);
		}
	};

	const hasMoreActionsMenu = showContractsMenuItem || showTransferMenuItem;

	return (
		<Box component={Paper} display="flex" flexDirection="column" padding={2} gap={1} mx={1}>
			{/* Linha 1: filtros principais + ações */}
			<Grid container spacing={2} direction={isSmallScreen ? "column" : "row"} alignItems={isSmallScreen ? "stretch" : "center"}>
				{showPeriodo && (
					<>
						<Grid item xs={12} sm={6} md={2}>
							<FormControl fullWidth size="small">
								<InputLabel id="period-label">Período</InputLabel>
								<Select labelId="period-label" value={periodType} label="Período" onChange={handlePeriodChange}>
									<MenuItem value="indefinido">Indefinido</MenuItem>
									<MenuItem value="15_dias">Últimos 15 dias</MenuItem>
									<MenuItem value="mes_anterior">Mês Anterior</MenuItem>
									<MenuItem value="1_mes">Mês Atual</MenuItem>
									<MenuItem value="proximo_mes">Próximo Mês</MenuItem>
									<MenuItem value="2_meses">Últimos 2 meses</MenuItem>
									<MenuItem value="3_meses">Últimos 3 meses</MenuItem>
									<MenuItem value="1_ano">Último ano</MenuItem>
									<MenuItem value="personalizado">Personalizado</MenuItem>
								</Select>
							</FormControl>
						</Grid>

						{periodType === "personalizado" && (
							<>
								<Grid item xs={12} sm={6} md={2}>
									<TextField
										fullWidth
										size="small"
										type="date"
										label="Data Inicial"
										value={dataIni}
										onChange={(e) => onChangeDataIni(e.target.value)}
									/>
								</Grid>
								<Grid item xs={12} sm={6} md={2}>
									<TextField
										fullWidth
										size="small"
										type="date"
										label="Data Final"
										value={dataFim}
										onChange={(e) => onChangeDataFim(e.target.value)}
									/>
								</Grid>
							</>
						)}
					</>
				)}

				{showStatus && (
					<Grid item xs={12} sm={6} md={2}>
						<FormControl fullWidth size="small">
							<InputLabel id="status-label">Status</InputLabel>
							<Select
								labelId="status-label"
								value={status}
								label="Status"
								onChange={(e) => onChangeStatus(e.target.value)}
							>
								<MenuItem value="Todos">Todos</MenuItem>
								<MenuItem value="active">Ativo</MenuItem>
								<MenuItem value="inactive">Inativo</MenuItem>
								<MenuItem value="pending">Pendente</MenuItem>
								<MenuItem value="blocked">Bloqueado</MenuItem>
								<MenuItem value="contract_pending">Contrato</MenuItem>
							</Select>
						</FormControl>
					</Grid>
				)}

				{showOrigem && (
					<Grid item xs={12} sm={6} md={2}>
						<FormControl fullWidth size="small">
							<InputLabel id="origem-label">Origem</InputLabel>
							<Select
								labelId="origem-label"
								value={origem}
								label="Origem"
								onChange={(e) => onChangeOrigem(e.target.value)}
							>
								<MenuItem value="Todos">Todos</MenuItem>
								<MenuItem value="web_app">Web</MenuItem>
								<MenuItem value="web_service">Integração</MenuItem>
							</Select>
						</FormControl>
					</Grid>
				)}

				{showUF && (
					<Grid item xs={12} sm={6} md={2}>
						<FormControl fullWidth size="small">
							<InputLabel id="uf-label">UF</InputLabel>
							<Select labelId="uf-label" value={uf} label="UF" onChange={(e) => onChangeUF(e.target.value)}>
								{RangeStates.map(({ value, label }) => (
									<MenuItem key={value} value={value}>
										{label}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>
				)}

				{!showInputSearch && (
					<Grid item xs={12} sm={12} md>
						<Box display="flex" justifyContent="flex-start" gap={1}>
							<Button variant="contained" startIcon={<SearchIcon />} onClick={onClickPesquisar}>
								Pesquisar
							</Button>
						</Box>
					</Grid>
				)}

				{(showButtonNew || hasMoreActionsMenu) && (
					<Grid item xs={12} sm="auto" md="auto" sx={{ ml: { md: "auto" } }}>
						<Box display="flex" gap={1} justifyContent={isSmallScreen ? "flex-start" : "flex-end"}>
							{hasMoreActionsMenu && (
								<>
									<IconButton
										aria-label="Abrir menu de ações"
										onClick={(e) => setMenuAnchorEl(e.currentTarget)}
									>
										<MoreVertIcon />
									</IconButton>
									<Menu anchorEl={menuAnchorEl} open={menuOpen} onClose={() => setMenuAnchorEl(null)}>
										{showContractsMenuItem && (
											<MenuItem
												disabled={!contractsEnabled}
												onClick={() => {
													setMenuAnchorEl(null);
													if (contractsEnabled) onClickContracts?.();
												}}
											>
												Contratos
											</MenuItem>
										)}
										{showTransferMenuItem && (
											<MenuItem
												disabled={!transferEnabled}
												onClick={() => {
													setMenuAnchorEl(null);
													if (transferEnabled) onClickTransfer?.();
												}}
											>
												Portabilidade
											</MenuItem>
										)}
									</Menu>
								</>
							)}

							{showButtonNew && (
								<Button variant="contained" color="primary" startIcon={<AddOutlinedIcon />} onClick={onClickNew}>
									{textoButtonNew}
								</Button>
							)}
						</Box>
					</Grid>
				)}
			</Grid>

			{/* Linha 2: gestor + pesquisa + botão */}
			<Grid container spacing={2} direction={isSmallScreen ? "column" : "row"} alignItems={isSmallScreen ? "stretch" : "center"}>
				{showManager && (
					<Grid item xs={12} sm={6} md={3}>
						<Autocomplete
							fullWidth
							disablePortal
							size="small"
							value={selectedManager}
							options={managerOptions}
							loading={managerLoading}
							onInputChange={(_, value) => setManagerSearch(value)}
							onChange={(_, value) => onChangeManagerId(value?.id ?? "")}
							renderInput={(params) => <TextField {...params} label="Gestor" />}
						/>
					</Grid>
				)}

				{showInputSearch && (
					<>
						<Grid item xs={12} sm={6} md={4} sx={{ maxWidth: { md: 520 } }}>
							<TextField
								fullWidth
								size="small"
								placeholder="Pesquisar..."
								value={textSearch}
								onChange={(e) => onChangeTextSearch?.(e.target.value)}
								InputProps={{
									startAdornment: (
										<IconButton aria-label="Pesquisar" edge="start" size="small" disabled>
											<SearchIcon />
										</IconButton>
									),
									endAdornment: (
										<IconButton
											aria-label="Limpar pesquisa"
											onClick={() => {
												onClearTextSearch?.();
												onChangeTextSearch?.("");
										}}
											edge="end"
											size="small"
										>
											<ClearIcon />
										</IconButton>
									),
								}}
							/>
						</Grid>
						<Grid item xs={12} sm="auto" md="auto">
							<Button variant="contained" startIcon={<SearchIcon />} onClick={onClickPesquisar}>
								Pesquisar
							</Button>
						</Grid>
					</>
				)}

				{!showInputSearch && (
					<Grid item xs={12} sm={12} md>
						<Box display="flex" justifyContent="flex-start" gap={1}>
							<Button variant="contained" startIcon={<SearchIcon />} onClick={onClickPesquisar}>
								Pesquisar
							</Button>
						</Box>
					</Grid>
				)}
			</Grid>

			{showPeriodo && dataIni && dataFim && (
				<Typography variant="body2" sx={{ mt: 1, ml: 1, fontStyle: "italic", color: "text.secondary" }}>
					Período: {format(parseISO(dataIni), "dd/MM/yyyy")} - {format(parseISO(dataFim), "dd/MM/yyyy")}
				</Typography>
			)}
		</Box>
	);
}
